import React from 'react';
import cn from 'classnames';
import { observer } from 'mobx-react';

import { RhinoEPGProgram } from '@kaltura-ott/tvpil-shared';

import { RecordIcon, SvgView } from 'components/presenters/icons';

import { calculateOffset } from '../../../../../../../../utils';

import { ellipsis } from './constants';
import { useProgramHooksCombiner } from './hooks';

import styles from './ProgramCard.module.scss';

interface Props {
  program: RhinoEPGProgram;
  channelName: string;
  channelNumber: number | string;
}

function ProgramCard({ program, channelNumber, channelName }: Props) {
  const {
    onMouseEnterHandle,
    onMouseLeaveHandle,
    clickHandler,
    refHandle,
    isLoading,
    epgParams,
    width,
    duration,
    startDate,
    isFutureDate,
    title,
    isDescriptionHidden,
    time,
    isOnlyMd,
    isWithoutMd,
    isCatchUpWithNoData,
    isAdultCatchUp,
    isRecordIconCondition,
    isMdIconCondition,
    isSeriesRecord,
    iconMDUrl,
    isTitleEllipsis,
    isTimeEllipsis,
    isAdultCathUpPlaybackEnabled,
    numberOfChannel
  } = useProgramHooksCombiner(program, channelNumber);

  return (
    <button
      ref={refHandle}
      style={{
        width,
        minWidth: width,
        left: calculateOffset(startDate, epgParams, width, duration),
      }}
      className={cn(styles.programCard, {
        [styles.loading]: isLoading,
        [styles.next]: isFutureDate,
        [styles.titleEllipsis]: isTitleEllipsis,
        [styles.timeEllipsis]: isTimeEllipsis,
        [styles.empty]: isDescriptionHidden,
        [styles.onlyMd]: isOnlyMd, // To display the MD icon with a short card length
        [styles.withoutMd]: isWithoutMd, // To hide the MD icon with a short card length
        [styles.noData]: isCatchUpWithNoData && !isLoading,
      })}
      onClick={clickHandler}
      onMouseEnter={onMouseEnterHandle}
      onMouseLeave={onMouseLeaveHandle}
      tabIndex={0}
      aria-label={`${channelName} ${numberOfChannel} ${title} ${time}`}
      type='button'
    >
      <div aria-hidden>
        <h5>{title}</h5>
        <span className={cn(styles.ellipsis, styles.title)}>{ellipsis}</span>

        <div className={styles.timeWrapper}>
          {((!isCatchUpWithNoData && !isAdultCatchUp) || isAdultCathUpPlaybackEnabled) && (
            <>
              <span className={styles.time}>{time}</span>
              {!isRecordIconCondition && <span className={cn(styles.ellipsis, styles.time)}>{ellipsis}</span>}
            </>
          )}
          {isRecordIconCondition && <RecordIcon active className={styles.recordIcon} multiple={!!isSeriesRecord} />}
          {isMdIconCondition && (
            <span className={styles.mdIcon}>
              <SvgView url={iconMDUrl} />
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default observer(ProgramCard);
